import { Theme, Typography } from '@mui/material';
import { FC } from 'react';
import { capitalizeFirstLetter } from 'string-utils';
import { makeStyles } from 'tss-react/mui';
import DateFormat from '../../../elements/Date/DateFormat';
import ExternalLinkButton from '../../../elements/ExternalLinkButton/ExternalLinkButton';
import InfoIcon from '../../../elements/InfoIcon/InfoIcon';
import OwnerCell from '../../../elements/TableCells/OwnerCell';
import { ArtifactInfo } from '../../types/artifacts-type';
import { getCicdSystemIconType } from '../../utils/artifacts-utils';
import ArtifactSections from './ArtifactSections';
import InfoIconSection from './InfoIconSection';
import Section from './Section';

const ArtifactPipelineExecutionInfo: FC<ArtifactPipelineExecutionInfoProps> = ({
  artifact,
}) => {
  const { classes } = useStyles();
  const cicdType = artifact.cicdDescription?.type;
  const CicdIcon = cicdType ? getCicdSystemIconType(cicdType) : null;
  const pipelineName =
    artifact.cicdDescription?.pipelineExecution?.pipelineName;
  const link = artifact.cicdDescription?.pipeline?.link;
  const executionId = artifact.cicdDescription?.pipelineExecution?.pipelineId;
  const pipelineTrigger = artifact.cicdDescription?.pipelineExecution?.pushType;
  const executedBy =
    artifact.cicdDescription?.pipelineExecution?.executedBy || '';
  const executionStartTime = new Date(
    Number(artifact.cicdDescription?.pipelineExecution?.createTime),
  );

  const executionLastActivity = artifact.cicdDescription?.pipelineExecution
    ?.updateTime
    ? new Date(artifact.cicdDescription?.pipelineExecution?.updateTime)
    : undefined;

  const status = artifact.cicdDescription?.pipelineExecution?.status;
  const pipelineLink = artifact.cicdDescription?.pipelineExecution?.link;
  const intermediateRegistry =
    artifact.cicdDescription?.pipelineExecution?.intermediateRegistry;

  const hash = artifact?.artifactInfo.hash || '';

  return (
    <div className={classes.artifactPipelineExecutionInfo}>
      <Typography variant='body2' color='text.primary' fontWeight={600}>
        Pipeline Execution
        <InfoIcon
          marginX={1}
          title={
            'Pipeline execution information describes the pipeline that created this artifact, including who initiated it, if it was updated and if the pipeline status was successful or failed.'
          }
        />
      </Typography>
      <ArtifactSections>
        <Section
          label='CI/CD'
          labelIcon={link && <ExternalLinkButton size='small' href={link} />}>
          <div className={classes.cicdType}>
            {CicdIcon && <CicdIcon height='1rem' />}
            <Typography variant='body1' color='text.primary'>
              {cicdType}
            </Typography>
          </div>
        </Section>

        <Section label='Execution ID'>#{executionId}</Section>
        <Section label='Pipeline Name'>
          <Typography variant='body1' color='text.primary'>
            {pipelineName}{' '}
            {pipelineLink && (
              <ExternalLinkButton
                size='small'
                href={pipelineLink}
                target='_blank'
              />
            )}
          </Typography>
        </Section>

        {intermediateRegistry && intermediateRegistry.length > 0 && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Files Changed
            </Typography>
            <div>
              {intermediateRegistry.map((inter, index) => (
                <Typography
                  variant='body1'
                  color='text.primary'
                  mb={0.5}
                  key={index}>
                  {inter}
                </Typography>
              ))}
            </div>
          </div>
        )}

        <Section label='Event Type'>
          {pipelineTrigger && capitalizeFirstLetter(pipelineTrigger)}
        </Section>
        <InfoIconSection
          label='Executed By'
          labelIcon='Who initiated the pipeline that created the artifact'>
          <OwnerCell owners={[executedBy]} />
        </InfoIconSection>

        <InfoIconSection
          label='Execution Start Time'
          labelIcon='When was the pipeline first initiated'>
          <DateFormat date={executionStartTime} color='text.primary' bigSize />
        </InfoIconSection>

        <InfoIconSection
          label='Last Activity On Execution'
          labelIcon='If the pipeline was updated, due to a manual or other operation, this will be reflected by this field'>
          <DateFormat
            date={executionLastActivity}
            color='text.primary'
            bigSize
          />
        </InfoIconSection>

        <InfoIconSection
          label='Status'
          labelIcon='The pipeline status (e.g Success/Failure), the artifact can be created even if the pipeline failed for some reason, like tests for example'>
          {status && capitalizeFirstLetter(status)}
        </InfoIconSection>
        {hash && <Section label='Hash'>{hash}</Section>}
      </ArtifactSections>
    </div>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  artifactPipelineExecutionInfo: {
    paddingBlock: theme.spacing(2),
  },
  cicdType: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
}));

export interface ArtifactPipelineExecutionInfoProps {
  artifact: ArtifactInfo;
}

export default ArtifactPipelineExecutionInfo;
